import { useCallback, useEffect, useRef } from 'react';

import { useStore } from '@nanostores/react';

import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import type { AssetsSegmentSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import type {
  AssetsHttpPostAssetsByAssetIdSegmentsHeader,
  AssetsSegmentSchemaWithId,
} from '@iconik-io/iconik-api/src/services/assets';

import { sendNewComment } from '../../../../redux/comment_attachments/thunks';
import type { SegmentEntityBase } from '../../../../redux/entities/normalized/segment';
import {
  getLatestActiveVersionId,
  selectAssetVersionsExtended,
} from '../../../../redux/entities/selectors/versions';
import { useAppDispatch, useAppSelector } from '../../../../redux/redux_hooks';
import { serializeUnifiedAnnotation } from '../../../../utils/annotation/serialize_unified_annotation';
import { filterExtendedVersionsBySelectedVersionIds } from '../../../../utils/filter_versions_by_selected_version_ids';
import { getPlaylistItemVersionIds } from '../../../../utils/get_playlist_item_version_ids';
import type { PlaylistItemVersionParam } from '../../../../utils/types/playlist';
import { useBeforeUnload } from '../../../hooks/use_before_unload';
import { useLatest } from '../../../hooks/use_latest';
import { usePrevious } from '../../../hooks/use_previous';
import type { PendingAttachments } from '../../shared/comment_attachments/hooks/use_pending_attachments';
import { useAppPlayerContext } from '../../shared/player/context';

type UseAutoAnnotationParams = {
  assetId: string;
  versionId: string | undefined;
  onCreated?: (segment: AssetsSegmentSchemaWithId) => void;
  shareLinkUserEmail: string | undefined;
  segment: AssetsSegmentSchemaPatched;
  canCreateComment?: boolean;
  originalPlaylistItemVersionId?: PlaylistItemVersionParam;
  attachments?: Pick<
    PendingAttachments,
    'claim' | 'complete' | 'release' | 'isSending' | 'hasAttachments'
  >;
};

const logger = DebugLogger.getInstance('auto_annotation');

type SegmentRangeParams = {
  rangeStartSeconds?: number;
  rangeEndSeconds?: number;
  timeSeconds?: number;
  pdfSlideNumber?: number;
};

function getSegmentRange({
  rangeStartSeconds,
  rangeEndSeconds,
  timeSeconds,
  pdfSlideNumber,
}: SegmentRangeParams) {
  if (pdfSlideNumber !== undefined) {
    return {
      start: pdfSlideNumber,
      end: pdfSlideNumber,
    };
  }

  let start = rangeStartSeconds ? rangeStartSeconds * 1000 : null;
  let end = rangeEndSeconds ? rangeEndSeconds * 1000 : null;
  if (start === null || end === null) {
    start = timeSeconds !== undefined ? timeSeconds * 1000 : null;
    end = timeSeconds !== undefined ? timeSeconds * 1000 : null;
  }

  return { start, end };
}

export function useAutoAnnotation({
  assetId,
  versionId,
  onCreated,
  shareLinkUserEmail,
  segment,
  canCreateComment = true,
  originalPlaylistItemVersionId,
  attachments,
}: UseAutoAnnotationParams) {
  const dispatch = useAppDispatch();
  const { store } = useAppPlayerContext();

  const currentSegment = useStore(store.segment.$currentSegment);
  const time = useStore(store.playableMedia.$targetTime);
  const frameAlignedTime = store.playableMedia.getFrameAlignedTime(time ?? 0);
  const playing = useStore(store.playableMedia.$playing);
  const currentPlaylistItem = useStore(store.playlist.$currentPlaylistItem);

  // PDF-specific handling: PDFs use slide numbers instead of time-based navigation
  const isPdf = currentPlaylistItem?.contentType === 'pdf';
  const currentSlide = useStore(store.slides.$currentSlide);
  const previousSlide = usePrevious(currentSlide);

  const previousTime = usePrevious(frameAlignedTime);
  const previousPlaying = usePrevious(playing);
  const previousAssetId = usePrevious(assetId);

  const previousSegment = usePrevious(currentSegment);
  const previousRangeStart = previousSegment?.range?.start;
  const previousRangeEnd = previousSegment?.range?.end;

  const isSaving = useRef(false);

  // Track the last known valid frame-aligned time. When an asset switch resets
  // the player store before the "assetId changed" effect fires, the live store
  // values are already stale (undefined), so we fall back to this ref.
  // Only update when the asset is stable: during navigation the store may
  // briefly reflect the incoming asset's time before the effect fires, which
  // would overwrite the value needed for the force-save on asset change.
  const lastKnownFrameTimeRef = useRef<number | undefined>(undefined);
  const assetIsStable =
    previousAssetId === undefined || assetId === previousAssetId;
  if (assetIsStable && time !== undefined && time !== null) {
    lastKnownFrameTimeRef.current = frameAlignedTime;
  }

  // Track the last known valid segment range for the same reason: when the
  // store resets on asset switch, the range is cleared before the forced save
  // runs, causing range comments to be saved as single frames.
  const lastKnownRangeRef = useRef<{ start: number; end: number } | undefined>(
    undefined,
  );
  if (currentSegment.range) {
    lastKnownRangeRef.current = currentSegment.range;
  } else if (assetIsStable && time !== undefined && time !== null) {
    // Store is active and range was explicitly cleared — reset the fallback.
    lastKnownRangeRef.current = undefined;
  }

  const assetVersions = useAppSelector((state) =>
    selectAssetVersionsExtended(state, assetId),
  );
  const selectedVersions = getPlaylistItemVersionIds(
    originalPlaylistItemVersionId,
  );

  const filteredVersionsBySelectedVersionIds =
    filterExtendedVersionsBySelectedVersionIds(assetVersions, selectedVersions);

  const latestVersionId = getLatestActiveVersionId(
    filteredVersionsBySelectedVersionIds,
  );
  const latestLatestVersionId = useLatest(latestVersionId);
  const latestAssetVersionId = useLatest(versionId);
  const latestAssetIdRef = useLatest(assetId);

  const latestSegment = useLatest(segment);
  const latestPreviousSegment = useLatest(previousSegment);
  const latestOnCreatedRef = useLatest(onCreated);
  const latestShareLinkUserEmailRef = useLatest(shareLinkUserEmail);
  const latestCanCreateCommentRef = useLatest(canCreateComment);
  const latestIsPdfRef = useLatest(isPdf);
  const latestAttachmentsRef = useLatest(attachments);
  const latestSegmentRangeRef = useLatest(
    getSegmentRange({
      rangeStartSeconds: previousRangeStart,
      rangeEndSeconds: previousRangeEnd,
      timeSeconds: previousTime,
      pdfSlideNumber: latestIsPdfRef.current ? previousSlide : undefined,
    }),
  );

  const saveAnnotation = useCallback(
    (
      segmentAssetId: string,
      {
        force = false,
        withoutFiles = false,
      }: { force?: boolean; withoutFiles?: boolean } = {},
    ) => {
      const drawing = serializeUnifiedAnnotation(
        store.segment.annotation.$personalDraftAnnotation.get(),
      );
      const latestSegmentObject = latestSegment.current;
      const previousSegmentData = latestPreviousSegment.current;
      const currentSegmentData = store.segment.$currentSegment.get();

      const isNewRangeSegment =
        !currentSegmentData?.id && currentSegmentData?.range;

      if (isNewRangeSegment && !force) {
        logger.debug('New segment, skipping auto-save');
        return false;
      }

      // Segment component handles auto-save for existing comments
      const isEditingExistingSegment =
        (currentSegmentData?.editing === true &&
          Boolean(currentSegmentData?.id)) ||
        (previousSegmentData?.editing === true &&
          Boolean(previousSegmentData?.id));

      if (isEditingExistingSegment) {
        logger.debug('Editing existing comment, skipping auto-save');
        return false;
      }

      const isReplyingToExistingSegment =
        (currentSegmentData?.replying === true &&
          Boolean(currentSegmentData?.id)) ||
        (previousSegmentData?.replying === true &&
          Boolean(previousSegmentData?.id));

      if (isReplyingToExistingSegment) {
        logger.debug('Replying to existing comment, skipping auto-save');
        return false;
      }

      const latestAttachments = latestAttachmentsRef.current;
      if (latestAttachments?.isSending()) {
        logger.debug('Sending a comment currently, skipping');
        return false;
      }
      const hasContent =
        Boolean(drawing) ||
        Boolean(latestSegmentObject.segment_text?.trim()) ||
        (!withoutFiles && Boolean(latestAttachments?.hasAttachments));

      if (!hasContent) {
        logger.debug('Annotation is empty, skipping');
        return false;
      }

      // The request would be rejected, and the draft annotation is cleared below
      // regardless, so saving here would discard the guest's work.
      if (!latestCanCreateCommentRef.current) {
        logger.debug('Commenting is not allowed yet, skipping');
        return false;
      }

      if (latestLatestVersionId.current !== latestAssetVersionId.current) {
        logger.debug('Version is not the latest, skipping');
        return false;
      }

      if (isSaving.current) {
        logger.debug('Saving another annotation currently, skipping');
        return false;
      }
      isSaving.current = true;

      // For forced saves (unmount, asset change), compute the range from live store
      // values so we capture the actual current position, not a stale cached value.
      // For regular (time-change-triggered) saves, use the precomputed range based
      // on previousTime, so the annotation lands on the frame *before* the seek.
      let segmentRange: { start: number | null; end: number | null };
      if (force) {
        const currentSegmentFromStore = store.segment.$currentSegment.get();
        const rawTime =
          store.playableMedia.$targetTime.get() ??
          store.playableMedia.$currentTime.get();
        // Prefer the pre-navigation snapshot over live store values. When
        // switching assets the store may reset $targetTime to 0 before this
        // save fires, making rawTime unreliable. The ref is guarded against
        // asset-change renders so it holds the correct pre-navigation position.
        let frameTime = lastKnownFrameTimeRef.current;
        if (frameTime === undefined && rawTime !== undefined) {
          frameTime = store.playableMedia.getFrameAlignedTime(rawTime);
        }
        // Similarly, if the store was reset the range will be undefined. Fall back
        // to the last known range captured before the reset.
        const rangeStart =
          currentSegmentFromStore.range?.start ??
          lastKnownRangeRef.current?.start;
        const rangeEnd =
          currentSegmentFromStore.range?.end ?? lastKnownRangeRef.current?.end;
        segmentRange = getSegmentRange({
          rangeStartSeconds: rangeStart,
          rangeEndSeconds: rangeEnd,
          timeSeconds: frameTime,
          pdfSlideNumber: latestIsPdfRef.current
            ? store.slides.$currentSlide.get()
            : undefined,
        });
      } else {
        segmentRange = latestSegmentRangeRef.current;
      }
      const { start, end } = segmentRange;
      const newSegment: SegmentEntityBase = {
        ...latestSegmentObject,
        time_start_milliseconds: start,
        time_end_milliseconds: end,
        drawing,
        has_drawing: Boolean(drawing),
      };

      const headers: AssetsHttpPostAssetsByAssetIdSegmentsHeader = {};
      if (latestShareLinkUserEmailRef.current) {
        headers['Share-User-Email'] = latestShareLinkUserEmailRef.current;
      }
      const claim = withoutFiles ? undefined : latestAttachments?.claim();
      const request = dispatch(
        sendNewComment({
          assetId: segmentAssetId,
          segment: newSegment,
          files: claim?.files.map(({ file }) => file) ?? [],
          headers,
          onFailure: () => 'publish',
        }),
      ).then(
        (savedSegment) => {
          if (claim) {
            latestAttachments?.complete(claim);
          }
          if (savedSegment?.id && latestAssetIdRef.current === segmentAssetId) {
            latestOnCreatedRef.current?.(savedSegment);
          }
        },
        (error: unknown) => {
          if (claim) {
            latestAttachments?.release(claim);
          }
          throw error;
        },
      );
      request.finally(() => {
        isSaving.current = false;
      });
      store.segment.annotation.clearPersonalDraftAnnotation();
      return true;
    },
    [],
  );

  useEffect(() => {
    const noRangeOrNotEditing =
      !currentSegment.range || !currentSegment.editing;

    const hasChangedTime =
      previousTime !== undefined && frameAlignedTime !== previousTime;

    const hasChangedPdfSlide =
      isPdf && currentSlide !== previousSlide && previousSlide !== undefined;
    const hasChangedSegment =
      currentSegment.id !== previousSegment?.id && previousSegment?.id;

    if (assetId !== previousAssetId && previousAssetId) {
      logger.debug(
        'Saving annotation, reason "asset changed"',
        previousAssetId,
        '→',
        assetId,
      );
      saveAnnotation(previousAssetId, { force: true });
    } else if (hasChangedSegment) {
      logger.debug(
        'Saving annotation, reason "segment changed"',
        previousSegment?.id,
        '→',
        currentSegment.id,
      );
      saveAnnotation(assetId);
    } else if (
      !playing &&
      (hasChangedTime || hasChangedPdfSlide) &&
      (noRangeOrNotEditing || currentSegment.editing)
    ) {
      logger.debug(
        'Saving annotation, reason "not playing, time/page changed"',
        previousTime,
        '→',
        frameAlignedTime,
      );
      saveAnnotation(assetId);
    } else if (!currentSegment.range && playing && !previousPlaying) {
      logger.debug('Saving annotation, started playing');
      saveAnnotation(assetId);
    }
  }, [
    currentSegment.range,
    currentSegment.editing,
    currentSegment.id,
    previousSegment?.id,
    playing,
    frameAlignedTime,
    previousTime,
    previousPlaying,
    assetId,
    previousAssetId,
    saveAnnotation,
    isPdf,
    currentSlide,
    previousSlide,
  ]);

  // Use case when we're leaving a component, responsible for auto annotations.
  useEffect(() => {
    return () => {
      logger.debug('Saving annotation, reason "component unmount"');
      saveAnnotation(assetId, { force: true });
    };
  }, [saveAnnotation, assetId]);

  // Save annotation when browser is about to close/reload
  useBeforeUnload({
    enabled: true,
    onBeforeUnload: (event) => {
      // Check actual current store values to avoid stale refs
      const currentDraftAnnotation =
        store.segment.annotation.$personalDraftAnnotation.get();
      const currentSegmentText = segment.segment_text?.trim();
      const drawing = serializeUnifiedAnnotation(currentDraftAnnotation);
      const hasText = Boolean(drawing) || Boolean(currentSegmentText);
      // Files cannot upload while the page unloads; they only keep the prompt.
      const hasPendingFiles = Boolean(attachments?.hasAttachments);

      if (!hasText && !hasPendingFiles) {
        logger.debug('Skipping beforeunload save, no content in current state');
        return false;
      }

      const saved =
        hasText && saveAnnotation(assetId, { force: true, withoutFiles: true });
      if (saved || hasPendingFiles) {
        event.preventDefault();
        logger.debug('Saving annotation, reason "browser unload"');
      }
      return saved || hasPendingFiles;
    },
  });
}
